import type { Situation } from "../types.js";
import { SIN_SENAL } from "./draft-situations.js";

/**
 * LAS SEIS SITUACIONES DE SEÑAL BASE que faltaban: una por cada tipo que el
 * alfa 1-6-9 no cubría. Con las siete de `draft-situations.ts`, el banco pasa a
 * tener señal propia para los nueve.
 *
 * CALIBRADAS el 17 de agosto de 2026 por el autor, con la misma escala del
 * alfa: +2 evidencia motivacional directa, +1 señal compartida o principalmente
 * conductual, -2 contraevidencia clara, y nunca ±3.
 *
 * MAPEO, el mismo que se usó para el alfa:
 *   Bloque D · «Señales favorables»            → la opción que apoya el tipo
 *   Bloque D · «Posibles falsos positivos»     → el distractor, que es la
 *                                                opción que PARECE ese tipo
 *                                                y es otro
 *   Bloque D · «Qué la debilitaría claramente» → la opción de contraevidencia
 *
 * TRES OPCIONES CON PAPEL DISTINTO, más una salida neutra. El distractor es la
 * pieza que hace esto un test y no un cuestionario: el canon nombra, tipo por
 * tipo, qué OTRO tipo produce la misma conducta por otro motivo, y esa opción
 * está redactada para que resulte igual de atractiva. Quien contesta por
 * conducta cae en ella; quien contesta por motivo, no.
 *
 * LA SALIDA NEUTRA no es adorno: sin ella la elección forzada obliga a puntuar
 * a alguien aunque la persona no se reconozca en nada (decisión 0.3, problema
 * ipsativo). Y tiene que ser neutra DE VERDAD — al calibrar el alfa se
 * descubrió que dos de ellas describían al 9 sin querer. Aquí todas dicen lo
 * mismo: que no hay ejemplo claro.
 *
 * En inglés, con el original español al lado.
 */

const V = "draft-nine-0.1";

export const SITUATIONS_NINE: Situation[] = [
  {
    id: "S8",
    prompt: "Someone close to you is struggling. What tends to happen?",
    context: "una relación cercana",
    purpose: "Señal base del 2: ayudar sin que se lo pidan, y no saber nombrar lo propio.",
    required: true,
    version: V,
    options: [
      {
        id: "S8_a",
        // «Ofrecer ayuda no solicitada de forma recurrente»
        text: "I've usually stepped in before they've asked me to.",
        signals: ["ayuda no solicitada recurrente"],
        evidenceTags: ["2:ayuda-no-solicitada"],
        counterevidenceTags: [],
        // Intervenir antes de que lo pidan: atención automática a la necesidad
        // ajena.
        patternWeights: { "2": 2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 2, señales favorables."
      },
      {
        id: "S8_b",
        // Falso positivo del 2: «ayudar por estrategia de imagen profesional
        // (tipo 3) sin el interés de fondo de conexión emocional»
        text: "I help, and I'd want it to be visibly well handled.",
        signals: ["ayuda como imagen competente"],
        evidenceTags: ["3:foco-en-logro"],
        counterevidenceTags: [],
        // La ayuda está condicionada a que el resultado quede visiblemente bien. No
        // es cercanía: es imagen.
        patternWeights: { "3": 2 },
        confidence: "medium",
        sourceStatus: "fuente",
        notes: "Distractor: el falso positivo que el canon nombra para el 2 — la misma " +
               "conducta de servicio movida por imagen, que es el 3."
      },
      {
        id: "S8_c",
        // «Qué la debilitaría: pedir ayuda directamente y poner límites sin culpa»
        text: "I say no when I haven't got it in me, and I ask for help myself without making a thing of it.",
        signals: ["pide ayuda y pone límites sin culpa"],
        evidenceTags: [],
        counterevidenceTags: ["2:pide-y-limita-sin-culpa"],
        // Decir no y pedir ayuda sin dramatizar contradice la negación de la
        // necesidad propia. Contraevidencia, no evidencia de otro tipo.
        patternWeights: { "2": -2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 2: «qué la debilitaría claramente». Contraevidencia."
      },
      {
        id: "S8_d",
        text: "I don't have a clear enough example to answer.",
        signals: [],
        evidenceTags: [],
        counterevidenceTags: [],
        patternWeights: SIN_SENAL,
        confidence: "low",
        sourceStatus: "inferencia",
        notes: "Salida neutra. Dato ausente, sin señal sobre ningún patrón."
      }
    ]
  },

  {
    id: "S9",
    prompt: "Think of something that genuinely didn't work out for you. How do you talk about it now?",
    context: "trabajo o un proyecto propio",
    purpose: "Señal base del 3: el fracaso no se sostiene sin reformular.",
    required: true,
    version: V,
    options: [
      {
        id: "S9_a",
        // «Dificultad marcada para hablar de un fracaso sin reformularlo en clave positiva»
        text: "As something I learned from. I find it hard to leave it sitting there as a plain failure.",
        signals: ["reformula el fracaso en positivo"],
        // Dos etiquetas porque la señal ES compartida: la misma conducta sostiene
        // las dos hipótesis, y enseñar sólo una haría el resultado menos
        // discutible de lo que es.
        evidenceTags: ["3:reformula-fracaso", "7:reencuadra-lo-doloroso"],
        counterevidenceTags: [],
        // Reencuadrar el fracaso como aprendizaje protege la narrativa de logro del
        // 3, pero es también la defensa clásica del 7. El texto no nombra reputación
        // ni rendimiento, así que 3:+2 daría falsos positivos. Ver la reescritura
        // propuesta al final del fichero.
        patternWeights: { "3": 1, "7": 1 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 3, señales favorables."
      },
      {
        id: "S9_b",
        // Falso positivo del 3: «alta exigencia con el resultado por estándar
        // interno (tipo 1) sin el interés de fondo de ser admirado/a»
        text: "As something that fell short of how it should have been done.",
        signals: ["exigencia por estándar interno"],
        evidenceTags: ["1:esta-bien-hecho"],
        counterevidenceTags: [],
        // El malestar es que no alcanzó cómo DEBÍA hacerse: criterio interior, no
        // fracaso visible.
        patternWeights: { "1": 2 },
        confidence: "medium",
        sourceStatus: "fuente",
        notes: "Distractor: el falso positivo que el canon nombra para el 3 — exigencia " +
               "por estándar, que es el 1, sin el interés de ser admirado."
      },
      {
        id: "S9_c",
        // «Qué la debilitaría: sostener una imagen poco favorecedora sin corregirla»
        text: "As a failure. I don't particularly need to put a better frame on it.",
        signals: ["sostiene una imagen poco favorecedora"],
        evidenceTags: [],
        counterevidenceTags: ["3:sostiene-mala-imagen"],
        // Nombrar un fracaso sin reconstruirlo para proteger imagen o trayectoria
        // contradice la gestión identitaria del fracaso.
        patternWeights: { "3": -2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 3: «qué la debilitaría claramente». Contraevidencia."
      },
      {
        id: "S9_d",
        text: "I don't have a clear enough example to answer.",
        signals: [],
        evidenceTags: [],
        counterevidenceTags: [],
        patternWeights: SIN_SENAL,
        confidence: "low",
        sourceStatus: "inferencia",
        notes: "Salida neutra."
      }
    ]
  },

  {
    id: "S10",
    prompt: "You're around people living fairly ordinary lives. What goes through your head?",
    context: "vida cotidiana",
    purpose: "Señal base del 4: comparación que termina en carencia, e intensidad.",
    required: true,
    version: V,
    options: [
      {
        id: "S10_a",
        // «Comparación frecuente con otros que termina en sensación de carencia»
        text: "I compare, and I usually come out of it feeling something is missing in me.",
        signals: ["comparación que termina en carencia"],
        evidenceTags: ["4:comparacion-carencia"],
        counterevidenceTags: [],
        // Compararse y concluir que falta algo esencial en uno mismo: envidia y
        // deficiencia identitaria.
        patternWeights: { "4": 2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 4, señales favorables."
      },
      {
        id: "S10_b",
        // Falso positivo del 4: «necesidad de distinción por estrategia de imagen
        // profesional (tipo 3) sin la carga emocional de fondo»
        text: "I want to stand out from it, though it's about how I'm seen rather than how I feel.",
        signals: ["distinción como estrategia de imagen"],
        evidenceTags: ["3:foco-en-logro"],
        counterevidenceTags: [],
        // Querer destacar por cómo se es visto, sin la vivencia de carencia: separa
        // la distinción de imagen del 3 de la identidad del 4.
        patternWeights: { "3": 2 },
        confidence: "medium",
        sourceStatus: "fuente",
        notes: "Distractor: el falso positivo que el canon nombra para el 4 — distinción " +
               "por imagen, que es el 3, sin la carga emocional."
      },
      {
        id: "S10_c",
        // «Qué la debilitaría: disfrute genuino de lo cotidiano sin intensificarlo»
        text: "Very little. I enjoy ordinary things without needing them to be more than they are.",
        signals: ["disfrute de lo ordinario"],
        evidenceTags: [],
        counterevidenceTags: ["4:disfruta-lo-ordinario"],
        // Disfrutar de lo ordinario sin que deba contener algo más contradice la
        // búsqueda de intensidad y significado especial.
        patternWeights: { "4": -2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 4: «qué la debilitaría claramente». Contraevidencia."
      },
      {
        id: "S10_d",
        text: "I don't have a clear enough example to answer.",
        signals: [],
        evidenceTags: [],
        counterevidenceTags: [],
        patternWeights: SIN_SENAL,
        confidence: "low",
        sourceStatus: "inferencia",
        notes: "Salida neutra."
      }
    ]
  },

  {
    id: "S11",
    prompt: "Someone asks you, directly and without warning, how you're really doing. What happens?",
    context: "una conversación personal",
    purpose: "Señal base del 5: incomodidad ante la petición emocional directa.",
    required: true,
    version: V,
    options: [
      {
        id: "S11_a",
        // «Incomodidad verbalizada ante peticiones emocionales directas»
        text: "I go uncomfortable. I'd rather have had time to work out what I think first.",
        signals: ["incomodidad ante petición emocional directa"],
        evidenceTags: ["5:incomoda-lo-emocional-directo"],
        counterevidenceTags: [],
        // Retirarse y elaborar antes de exponerse: conservación de recursos,
        // privacidad, preparación.
        patternWeights: { "5": 2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 5, señales favorables."
      },
      {
        id: "S11_b",
        // Falso positivo del 5: «repliegue por agotamiento puntual o introversión
        // situacional, sin el patrón sostenido de acumulación y aislamiento»
        text: "Depends on the day. If I'm worn out I'll keep it short, otherwise I don't mind.",
        signals: ["repliegue puntual por agotamiento"],
        evidenceTags: [],
        counterevidenceTags: [],
        // SIN_SENAL. La retirada depende explícitamente del cansancio puntual: no
        // demuestra una estrategia sostenida.
        patternWeights: SIN_SENAL,
        confidence: "medium",
        sourceStatus: "fuente",
        notes: "Distractor: el falso positivo que el canon nombra para el 5 — repliegue " +
               "puntual, sin el patrón sostenido. Puede acabar en cero: es lo que el " +
               "autor decide al calibrar."
      },
      {
        id: "S11_c",
        // «Qué la debilitaría: comprometerse emocionalmente sin preparación previa»
        text: "I answer properly, there and then. I don't need to have prepared it.",
        signals: ["compromiso emocional sin preparación"],
        evidenceTags: [],
        counterevidenceTags: ["5:se-compromete-sin-preparar"],
        // Responder de inmediato y sin preparación contradice la necesidad de
        // procesar y administrar la exposición.
        patternWeights: { "5": -2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 5: «qué la debilitaría claramente». Contraevidencia."
      },
      {
        id: "S11_d",
        text: "I don't have a clear enough example to answer.",
        signals: [],
        evidenceTags: [],
        counterevidenceTags: [],
        patternWeights: SIN_SENAL,
        confidence: "low",
        sourceStatus: "inferencia",
        notes: "Salida neutra."
      }
    ]
  },

  {
    id: "S12",
    prompt: "A conversation turns painful and there's no quick way out of it. What do you do?",
    context: "una conversación difícil",
    purpose: "Señal base del 7: desviar el malestar con humor o cambio de tema.",
    required: true,
    version: V,
    options: [
      {
        id: "S12_a",
        // «Dificultad marcada para permanecer en una conversación dolorosa sin
        //  desviarla con humor o cambio de tema»
        text: "I lighten it — a joke, or I move us on to something else.",
        signals: ["desvía el malestar con humor o cambio de tema"],
        evidenceTags: ["7:desvia-el-malestar"],
        counterevidenceTags: [],
        // Aligerar o cambiar de tema cuando aparece dolor: reencuadre y escape de lo
        // desagradable.
        patternWeights: { "7": 2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 7, señales favorables."
      },
      {
        id: "S12_b",
        // Falso positivo del 7: «alta energía social por estrategia de liderazgo
        // o carisma (tipo 3 o tipo 8) sin el patrón de evitación de fondo»
        text: "I take charge of it and get us to a decision.",
        signals: ["energía social por liderazgo"],
        evidenceTags: ["3:eficacia-sobre-permanencia", "8:toma-el-mando"],
        counterevidenceTags: [],
        // Tomar el mando puede ser eficiencia del 3 o control del 8. La conducta no
        // permite elegir: señal compartida débil.
        patternWeights: { "3": 1, "8": 1 },
        confidence: "medium",
        sourceStatus: "fuente",
        notes: "Distractor: el falso positivo que el canon nombra para el 7 — energía " +
               "por liderazgo (3 u 8), sin la evitación de fondo."
      },
      {
        id: "S12_c",
        // «Qué la debilitaría: permanecer con una experiencia difícil sin buscar salida»
        text: "I stay in it. Uncomfortable, but I don't need a way out.",
        signals: ["permanece en lo difícil"],
        evidenceTags: [],
        counterevidenceTags: ["7:permanece-en-lo-dificil"],
        // Permanecer en el dolor sin necesitar salida contradice la evitación que
        // organiza el patrón.
        patternWeights: { "7": -2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 7: «qué la debilitaría claramente». Contraevidencia."
      },
      {
        id: "S12_d",
        text: "I don't have a clear enough example to answer.",
        signals: [],
        evidenceTags: [],
        counterevidenceTags: [],
        patternWeights: SIN_SENAL,
        confidence: "low",
        sourceStatus: "inferencia",
        notes: "Salida neutra."
      }
    ]
  },

  {
    id: "S13",
    prompt: "Someone starts telling you how to do something that's yours to decide. What's the first thing you feel?",
    context: "trabajo o una relación",
    purpose: "Señal base del 8: reacción rápida ante la señal de ser controlado.",
    required: true,
    version: V,
    options: [
      {
        id: "S13_a",
        // «Reacción rápida y directa ante cualquier señal de ser controlado/a
        //  o subestimado/a»
        text: "Something goes up in me straight away, and it shows.",
        signals: ["reacción rápida ante el control"],
        evidenceTags: ["8:reacciona-al-control"],
        counterevidenceTags: [],
        // Reacción visceral e inmediata ante la intromisión en el propio dominio:
        // sensibilidad al control.
        patternWeights: { "8": 2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 8, señales favorables."
      },
      {
        id: "S13_b",
        // Falso positivo del 8: «confrontación puntual por convicción de
        // principios (tipo 1) sin el patrón sostenido de necesidad de control»
        text: "It depends whether they're right. If they're not, I'll say so on the merits.",
        signals: ["confrontación por principio"],
        evidenceTags: ["1:esta-bien-hecho"],
        counterevidenceTags: [],
        // La confrontación depende de si la otra persona TIENE RAZÓN. El criterio es
        // mérito, no autonomía.
        patternWeights: { "1": 2 },
        confidence: "medium",
        sourceStatus: "fuente",
        notes: "Distractor: el falso positivo que el canon nombra para el 8 — confrontar " +
               "por convicción, que es el 1, sin la necesidad sostenida de control."
      },
      {
        id: "S13_c",
        // «Qué la debilitaría: ceder el control o mostrar vulnerabilidad sin
        //  que se viva como derrota»
        text: "Not much. I can hand it over, or say I don't know, without it costing me anything.",
        signals: ["cede el control sin vivirlo como derrota"],
        evidenceTags: [],
        counterevidenceTags: ["8:cede-control-sin-derrota"],
        // Ceder control o admitir desconocimiento sin coste interno contradice la
        // protección de autonomía y fuerza.
        patternWeights: { "8": -2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 8: «qué la debilitaría claramente». Contraevidencia."
      },
      {
        id: "S13_d",
        text: "I don't have a clear enough example to answer.",
        signals: [],
        evidenceTags: [],
        counterevidenceTags: [],
        patternWeights: SIN_SENAL,
        confidence: "low",
        sourceStatus: "inferencia",
        notes: "Salida neutra."
      }
    ]
  },

  // ══════════════════════════════════════════════════════════════════════════
  //  S14, S15 y S16 — LA SEGUNDA SITUACIÓN DEL 2, EL 4 Y EL 5.
  //
  //  No estaban previstas. Salieron de medir el banco después de calibrarlo:
  //  con una sola situación capaz de darles evidencia positiva, esos tres tipos
  //  eran INALCANZABLES — el criterio exige dos situaciones distintas, así que
  //  ninguna combinación de respuestas podía producirlos.
  //
  //  El 1 y el 3 no tenían el problema porque aparecen como distractores en
  //  situaciones ajenas (el 1 en S9_b y S13_b, el 3 en S8_b y S10_b) y así
  //  reunían cobertura de rebote. El 2, el 4 y el 5 no le hacían de distractor
  //  a nadie.
  //
  //  Cada una usa la SEGUNDA señal favorable del Bloque D, que estaba sin usar:
  //  las «Señales favorables» del canon traen dos separadas por punto y coma, y
  //  las seis situaciones anteriores sólo gastaron la primera.
  //
  //  CALIBRADAS por el autor el 17 de agosto de 2026, con el mismo criterio.
  // ══════════════════════════════════════════════════════════════════════════
  {
    id: "S14",
    prompt: "Someone asks you directly what you need. What happens?",
    context: "una relación cercana",
    purpose: "Segunda señal del 2: no saber nombrar lo propio cuando se pregunta de frente.",
    required: true,
    version: V,
    options: [
      {
        id: "S14_a",
        // «Dificultad para nombrar una necesidad propia cuando se pregunta directamente»
        text: "I go blank. I could tell you what everyone else needs, but not that.",
        signals: ["no sabe nombrar la necesidad propia"],
        evidenceTags: ["2:no-nombra-lo-propio"],
        counterevidenceTags: [],
        // Saber nombrar lo que necesitan los demás y quedarse en blanco con lo
        // propio: la atención automática va hacia fuera. Bloque D del 2, segunda
        // señal favorable.
        patternWeights: { "2": 2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 2, segunda señal favorable."
      },
      {
        id: "S14_b",
        // Distractor: el Bloque D del 5 dice «incomodidad verbalizada ante
        // peticiones emocionales directas». Misma incomodidad, otro motivo:
        // el 2 no encuentra la necesidad, el 5 no quiere exponerla sin preparar.
        text: "I know what I need. I'd just rather not be put on the spot about it.",
        signals: ["incomodidad ante la petición directa, con la necesidad clara"],
        evidenceTags: ["5:incomoda-lo-emocional-directo"],
        counterevidenceTags: [],
        // La persona SABE qué necesita; el problema es quedar expuesta o invadida al
        // expresarlo. Eso es reserva y administración de la exposición — señal del
        // 5, no del 2. NO es equivalente a S11_b, que se quedó a cero porque allí la
        // retirada dependía del cansancio circunstancial y aquí no.
        patternWeights: { "5": 2 },
        confidence: "medium",
        sourceStatus: "fuente",
        notes: "Distractor tomado del Bloque D del 5: la misma incomodidad ante la " +
               "petición directa, movida por reserva y no por no encontrar lo propio."
      },
      {
        id: "S14_c",
        // «Qué la debilitaría: pedir ayuda directamente sin culpa»
        text: "I say it. It doesn't cost me anything to name it.",
        signals: ["nombra la necesidad sin coste"],
        evidenceTags: [],
        counterevidenceTags: ["2:pide-y-limita-sin-culpa"],
        // Nombrar la necesidad sin que cueste contradice la dificultad para pedir.
        // Bloque D del 2: «qué la debilitaría claramente».
        patternWeights: { "2": -2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 2: «qué la debilitaría claramente». Contraevidencia."
      },
      {
        id: "S14_d",
        text: "I don't have a clear enough example to answer.",
        signals: [],
        evidenceTags: [],
        counterevidenceTags: [],
        patternWeights: SIN_SENAL,
        confidence: "low",
        sourceStatus: "inferencia",
        notes: "Salida neutra."
      }
    ]
  },

  {
    id: "S15",
    prompt: "You're telling someone about something that happened to you this week. How does it tend to come out?",
    context: "conversación cotidiana",
    purpose: "Segunda señal del 4: lenguaje intenso para lo ordinario.",
    required: true,
    version: V,
    options: [
      {
        id: "S15_a",
        // «Lenguaje intenso o dramático para describir experiencias cotidianas»
        text: "Vividly. People sometimes tell me it sounded worse — or better — than it was.",
        signals: ["lenguaje intenso para lo cotidiano"],
        evidenceTags: ["4:lenguaje-intenso"],
        counterevidenceTags: [],
        // Lenguaje intenso para lo cotidiano, hasta el punto de que otros lo notan.
        // Bloque D del 4, segunda señal favorable.
        patternWeights: { "4": 2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 4, segunda señal favorable."
      },
      {
        id: "S15_b",
        // Distractor: el 7 también cuenta en grande, pero seleccionando lo
        // bueno. El canon no nombra el par 4↔7 — de ahí `inferencia`.
        text: "Upbeat. I tend to tell the good bits and skip the rest.",
        signals: ["relato en positivo, selecciona lo bueno"],
        evidenceTags: ["7:reencuadra-lo-doloroso"],
        counterevidenceTags: [],
        // +1 POR AMBIGÜEDAD TEÓRICA, y ese es el motivo. Describe selección positiva
        // y omisión de lo doloroso, pero no demuestra POR QUÉ se hace: cabe igual la
        // gestión de imagen del 3, o simple estilo narrativo. Y no hay
        // diferenciación canónica 4↔7 detrás del reactivo — me lo inventé por
        // analogía — así que +2 sería inflar una hipótesis hasta el peso de una
        // fuente. Que con +1 el tipo 7 pase a ser alcanzable es una CONSECUENCIA
        // comprobada, no la razón: el banco demuestra alcanzabilidad, nunca validez.
        // Si el piloto no sostiene este reactivo, se cae aunque el 7 deje de salir.
        patternWeights: { "7": 1 },
        confidence: "low",
        sourceStatus: "inferencia",
        notes: "[HIPÓTESIS] Distractor del 7: la misma intensidad al contar, pero " +
               "seleccionando lo agradable. El canon NO trata el par 4↔7, así que esto " +
               "es inferencia y va a vigilar en el piloto."
      },
      {
        id: "S15_c",
        // «Qué la debilitaría: disfrute de lo cotidiano sin intensificarlo»
        text: "Plainly. It was an ordinary week and it comes out as one.",
        signals: ["relato llano de lo ordinario"],
        evidenceTags: [],
        counterevidenceTags: ["4:disfruta-lo-ordinario"],
        // Contar una semana corriente como corriente contradice la necesidad de
        // intensificar. Bloque D del 4: «qué la debilitaría claramente».
        patternWeights: { "4": -2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 4: «qué la debilitaría claramente». Contraevidencia."
      },
      {
        id: "S15_d",
        text: "I don't have a clear enough example to answer.",
        signals: [],
        evidenceTags: [],
        counterevidenceTags: [],
        patternWeights: SIN_SENAL,
        confidence: "low",
        sourceStatus: "inferencia",
        notes: "Salida neutra."
      }
    ]
  },

  {
    id: "S16",
    prompt: "Something new lands on you that you'd have to act on. What's your actual first move?",
    context: "trabajo o vida práctica",
    purpose: "Segunda señal del 5: investigar antes de actuar.",
    required: true,
    version: V,
    options: [
      {
        id: "S16_a",
        // «Necesidad marcada de investigar antes de actuar»
        text: "I go and find out about it properly before I do anything.",
        signals: ["investiga antes de actuar"],
        evidenceTags: ["5:investiga-antes-de-actuar"],
        counterevidenceTags: [],
        // Investigar antes de actuar: preparación y conservación de recursos. Bloque
        // D del 5, primera señal favorable.
        patternWeights: { "5": 2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 5, primera señal favorable — la que S11 no usó."
      },
      {
        id: "S16_b",
        // Distractor del par 5↔6, que el canon SÍ trata: «acumular conocimiento
        // en soledad favorece 5; buscar apoyo o consulta externa favorece 6».
        text: "I find someone who's done it before and ask them.",
        signals: ["consulta externa antes de actuar"],
        evidenceTags: ["6:consulta-repetida"],
        counterevidenceTags: [],
        // No recibe +2 por ser distractor, sino porque EXTERNALIZA LA CONFIANZA
        // COGNITIVA: frente al 5, que intenta comprender por sí mismo, el 6 busca
        // experiencia contrastada y apoyo. Es la diferencia canónica del par 5↔6, y
        // además triangulada contra el Enneagram Institute («Misidentifying 5 and
        // 6»), que es una segunda fuente — algo que a este repositorio le falta casi
        // en todas partes.
        patternWeights: { "6": 2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Distractor del Bloque E 5↔6: misma cautela, resuelta por consulta " +
               "externa en vez de por estudio propio. TRIANGULADO contra el Enneagram " +
               "Institute, «Misidentifying 5 and 6» — segunda fuente, que es justo lo " +
               "que el resto del contenido no tiene."
      },
      {
        id: "S16_c",
        // «Qué la debilitaría: comprometerse sin preparación previa exhaustiva»
        text: "I start, and work it out as I go.",
        signals: ["actúa sin preparación previa"],
        evidenceTags: [],
        counterevidenceTags: ["5:se-compromete-sin-preparar"],
        // Empezar y resolver sobre la marcha contradice la necesidad de preparación
        // previa. Bloque D del 5: «qué la debilitaría claramente».
        patternWeights: { "5": -2 },
        confidence: "high",
        sourceStatus: "fuente",
        notes: "Bloque D del 5: «qué la debilitaría claramente». Contraevidencia."
      },
      {
        id: "S16_d",
        text: "I don't have a clear enough example to answer.",
        signals: [],
        evidenceTags: [],
        counterevidenceTags: [],
        patternWeights: SIN_SENAL,
        confidence: "low",
        sourceStatus: "inferencia",
        notes: "Salida neutra."
      }
    ]
  }
];

/**
 * REESCRITURA PROPUESTA PARA S9_a, sin aplicar.
 *
 * Tal como está, «As something I learned from…» describe igual de bien la
 * reformulación de logro del 3 y la defensa clásica del 7 ante lo doloroso —
 * por eso está calibrada 3:+1 y 7:+1, que es lo honesto pero deja al 3 sin
 * señal fuerte propia en su propia situación.
 *
 * Para que sea 3:+2 tendría que nombrar la amenaza de valor o de imagen:
 *
 *   "As something I learned from. I don't like people seeing me as someone
 *    who failed and simply stayed there."
 *
 * No se aplica ahora porque cambia el texto que la persona lee, y eso invalida
 * la calibración de esa opción: habría que volver a ponderarla. Va al piloto
 * como variante a contrastar.
 */
